import { useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { ArrowRight, Info, ShieldCheck, XIcon } from '@music-vine/cadence/icons';
import { motion } from 'motion/react';
import { Button, Input, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@music-vine/cadence/ui';
import Image from 'next/image';
import { setAsDismissedInStorage } from '@/components/PagePopup/utils/setAsDismissedInStorage';
import { getMaxSafelistedChannels } from '@/components/TopBar/SafelistYourChannel/getMaxSafelistedChannels';
import { useSearchYoutubeChannel } from '@/components/UBSafelist/hooks/useSearchYoutubeChannel';
import { useWhitelistChannel } from '@/components/UBSafelist/hooks/useWhitelistChannel';
import { useYoutubeChannelData } from '@/components/UBSafelist/hooks/useYoutubeChannelData';
import { PLACEHOLDER_IMG_URL } from '@/constants/images';
import { useSubscriptionType } from '@/hooks/auth';
import { cn } from '@/lib/utils';
import { SAFELIST_PROMPT_OPT_OUT_KEY } from './useSafelistChannelPromptEligibility';
import type { YouTubeChannelSnippet } from '@/components/UBSafelist/types';
import type { SubscriptionType } from '@/types/subscription';
const dismissDialogue = () => {
  window.postMessage({
    type: 'DISMISS_DIALOGUE'
  }, window.location.origin);
};
const PLAN_DISPLAY_NAMES: Partial<Record<SubscriptionType, string>> = {
  Essentials: 'Essentials',
  Creator: 'Creator',
  Pro: 'Pro',
  Business: 'Business'
};
const CHANNEL_URL_PREFIX_REGEX = /^(?:https?:\/\/)?(?:www\.)?youtube\.com\//i;
const LEADING_AT_REGEX = /^@/;
const CHANNEL_ID_LENGTH = 24;

// Matches the minimum query length useSearchYoutubeChannel fetches for. The
// search query keeps its previous results (keepPreviousData) below this
// length, so the dropdown must hide itself once the input is cleared.
const MIN_SEARCH_LENGTH = 3;

// No locale param — Google localises the article to the visitor itself.
const CHANNEL_ID_HELP_URL = 'https://support.google.com/youtube/answer/3250431';
type SafelistMode = 'channel-id' | 'name';
interface SelectedChannel {
  channelId: string;
  handle?: string;
  thumbnailUrl?: string;
  title: string;
}
const ChannelBox = ({
  channel,
  onChange
}: {
  channel: SelectedChannel;
  onChange: () => void;
}) => <div className="flex w-full items-center gap-3 rounded-lg bg-gray-50 px-3 py-4 dark:bg-gray-800" data-sentry-component="ChannelBox" data-sentry-source-file="SafelistChannelPromptContent.tsx">
    <Image alt={`Channel thumbnail for ${channel.title}`} className="size-10 shrink-0 rounded-full bg-gray-200 object-cover dark:bg-gray-700" height={40} onError={e => {
    e.currentTarget.src = PLACEHOLDER_IMG_URL;
  }} src={channel.thumbnailUrl || PLACEHOLDER_IMG_URL} width={40} data-sentry-element="Image" data-sentry-source-file="SafelistChannelPromptContent.tsx" />
    <div className="flex min-w-0 flex-1 flex-col">
      <p className="truncate font-semibold text-gray-950 text-sm dark:text-white">
        {channel.title}
      </p>
      {!!channel.handle && <p className="truncate text-gray-700 text-sm dark:text-gray-300">
          {channel.handle}
        </p>}
    </div>
    <button className="shrink-0 cursor-pointer text-pink-600 underline dark:text-white" onClick={onChange} type="button">
      Change
    </button>
  </div>;

/**
 * Body of the safelist-channel prompt, shared by the desktop dialogue and the
 * mobile drawer (`inDrawer` tightens spacing/typography for the drawer).
 * Lets the user find their YouTube channel by name/URL or by channel ID and
 * safelist it in place, or opt out of the prompt permanently.
 */
export const SafelistChannelPromptContent = ({
  inDrawer = false
}: {
  inDrawer?: boolean;
}) => {
  const subscriptionType = useSubscriptionType();
  const [mode, setMode] = useState<SafelistMode>('name');
  const [searchValue, setSearchValue] = useState('');
  const [channelIdValue, setChannelIdValue] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<SelectedChannel | null>(null);

  // Animate the dialogue's height when the content changes size (switching
  // tabs, selecting a channel, the CTA appearing). The wrapper animates to
  // the measured full height of the content; overflow is only clipped while
  // the animation runs so the floating search dropdown isn't cut off at rest.
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [isAnimatingHeight, setIsAnimatingHeight] = useState(false);
  useEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(() => {
      // offsetHeight rather than the observed contentRect: it includes the
      // content's padding, which the animated wrapper needs to match.
      setContentHeight(element.offsetHeight);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const searchQuery = searchValue.trim().replace(CHANNEL_URL_PREFIX_REGEX, '').replace(LEADING_AT_REGEX, '');
  const {
    data: searchResults
  } = useSearchYoutubeChannel(searchQuery);

  // Clicking outside the search area dismisses the results dropdown (it can
  // otherwise cover the CTAs below). Typing or refocusing brings it back.
  const searchAreaRef = useRef<HTMLDivElement>(null);
  const [isResultsDismissed, setIsResultsDismissed] = useState(false);
  useClickAway(searchAreaRef, () => setIsResultsDismissed(true));
  const trimmedChannelId = channelIdValue.trim();
  const hasFullChannelId = trimmedChannelId.length === CHANNEL_ID_LENGTH;
  const {
    data: channelIdData
  } = useYoutubeChannelData(hasFullChannelId ? trimmedChannelId : '');
  const {
    whitelistChannel,
    isWhitelisting,
    error
  } = useWhitelistChannel();
  const maxChannels = getMaxSafelistedChannels({
    subscriptionType
  });
  const planName = PLAN_DISPLAY_NAMES[subscriptionType];

  // YouTube's search API omits customUrl from its snippets, so a channel
  // picked from the search results arrives without a handle. Fetch the full
  // channel data (which does include customUrl) to fill it in.
  const {
    data: selectedChannelData
  } = useYoutubeChannelData(selectedChannel && !selectedChannel.handle ? selectedChannel.channelId : '');
  const selectedChannelWithHandle: SelectedChannel | null = selectedChannel ? {
    ...selectedChannel,
    handle: selectedChannel.handle ?? (selectedChannelData?.channelId === selectedChannel.channelId ? selectedChannelData.customUrl : undefined)
  } : null;
  const channelFromId: SelectedChannel | null = channelIdData ? {
    channelId: channelIdData.channelId,
    title: channelIdData.title,
    handle: channelIdData.customUrl,
    thumbnailUrl: channelIdData.thumbnails?.default?.url
  } : null;

  // Per the design, the opt-out link is hidden once a channel is picked in
  // the active tab — the CTA takes over as the dialogue's action.
  const hasSelectedChannel = mode === 'name' ? !!selectedChannel : !!channelFromId;
  const handleTabChange = (value: string) => {
    setMode(value as SafelistMode);
    setSearchValue('');
    setChannelIdValue('');
  };
  const handleSelectChannel = (snippet: YouTubeChannelSnippet) => {
    setSelectedChannel({
      channelId: snippet.channelId,
      title: snippet.title,
      handle: snippet.customUrl,
      thumbnailUrl: snippet.thumbnails?.default?.url
    });
  };
  const handleSafelist = async (channel: SelectedChannel) => {
    try {
      await whitelistChannel({
        channelId: channel.channelId,
        channelTitle: channel.title
      });
      toast.success('Channel safelisted', {
        closeButton: false
      });
      dismissDialogue();
    } catch {
      // Surfaced through `error` from useWhitelistChannel.
    }
  };
  const handleOptOut = () => {
    setAsDismissedInStorage({
      key: SAFELIST_PROMPT_OPT_OUT_KEY,
      storage: 'localStorage'
    });
    dismissDialogue();
  };
  const safelistCta = (channel: SelectedChannel | null) => !!channel && <Button className="mt-2 w-full" disabled={isWhitelisting} fontSize="base" onClick={() => handleSafelist(channel)} size="lg" type="button" variant="bold">
        {isWhitelisting ? 'Safelisting…' : 'Safelist channel'}
      </Button>;
  return <motion.div animate={contentHeight === null ? undefined : {
    height: contentHeight
  }} initial={false} onAnimationComplete={() => setIsAnimatingHeight(false)} onAnimationStart={() => setIsAnimatingHeight(true)} style={{
    overflow: isAnimatingHeight ? 'hidden' : 'visible'
  }} transition={{
    type: 'spring',
    duration: 0.4,
    bounce: 0
  }} data-sentry-element="motion.div" data-sentry-component="SafelistChannelPromptContent" data-sentry-source-file="SafelistChannelPromptContent.tsx">
      <div className={cn('flex flex-col gap-4', inDrawer ? 'px-2 pb-2' : 'px-10 pt-10 pb-7')} ref={contentRef}>
        <div className={cn('flex gap-3', inDrawer ? 'flex-col' : 'items-center')}>
          <div className="flex size-13 shrink-0 items-center justify-center rounded-[10px] bg-green-50 dark:bg-green-950">
            <ShieldCheck className="size-8 text-green-600 dark:text-green-400" data-sentry-element="ShieldCheck" data-sentry-source-file="SafelistChannelPromptContent.tsx" />
          </div>
          {!inDrawer && <p className="font-semibold text-green-600 text-lg dark:text-green-400">
              CHANNEL SAFELIST
            </p>}
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="font-heading font-semibold text-2xl text-gray-950 tracking-tight dark:text-white">
            Safelist your YouTube channel
          </h2>
          <p className={cn('text-gray-950 dark:text-gray-50', inDrawer ? 'text-sm' : 'text-base')}>
            As an Uppbeat {planName ? `${planName} ` : ''}subscriber, you can{' '}
            <span className="font-semibold">
              connect {maxChannels} YouTube{' '}
              {maxChannels === 1 ? 'channel' : 'channels'}
            </span>{' '}
            to prevent copyright claims on any videos you publish using Uppbeat
            tracks.
          </p>
        </div>
        <Tabs className="mt-3" onValueChange={handleTabChange} value={mode} data-sentry-element="Tabs" data-sentry-source-file="SafelistChannelPromptContent.tsx">
          <TabsList className="h-auto w-full rounded-lg border border-gray-150 bg-gray-50 p-1 dark:border-gray-800 dark:bg-gray-900" data-sentry-element="TabsList" data-sentry-source-file="SafelistChannelPromptContent.tsx">
            <TabsTrigger className={cn('flex-1 rounded-md border border-transparent py-2 text-gray-700 data-[state=active]:border-gray-150 dark:text-gray-200 dark:data-[state=active]:border-gray-700', inDrawer ? 'text-sm' : 'text-base')} value="name" data-sentry-element="TabsTrigger" data-sentry-source-file="SafelistChannelPromptContent.tsx">
              Search by name
            </TabsTrigger>
            <TabsTrigger className={cn('flex-1 rounded-md border border-transparent py-2 text-gray-700 data-[state=active]:border-gray-150 dark:text-gray-200 dark:data-[state=active]:border-gray-700', inDrawer ? 'text-sm' : 'text-base')} value="channel-id" data-sentry-element="TabsTrigger" data-sentry-source-file="SafelistChannelPromptContent.tsx">
              Enter channel ID
            </TabsTrigger>
          </TabsList>

          <TabsContent className="mt-5 data-[state=inactive]:hidden" value="name" data-sentry-element="TabsContent" data-sentry-source-file="SafelistChannelPromptContent.tsx">
            <motion.div animate={{
            opacity: 1
          }} className="flex flex-col gap-2" initial={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} data-sentry-element="motion.div" data-sentry-source-file="SafelistChannelPromptContent.tsx">
              <p className="font-semibold text-gray-950 text-lg dark:text-white">
                YouTube channel URL or handle
              </p>
              {selectedChannelWithHandle ? <ChannelBox channel={selectedChannelWithHandle} onChange={() => setSelectedChannel(null)} /> : <div className="relative" ref={searchAreaRef}>
                  <div className="relative">
                    <Input autoComplete="off" className={cn(!!searchValue && 'pr-10')} name="youtube_channel" onChange={event => {
                  setSearchValue(event.target.value);
                  setIsResultsDismissed(false);
                }} onFocus={() => setIsResultsDismissed(false)} placeholder="youtube.com/@yourchannel" type="text" value={searchValue} />
                    {!!searchValue && <button aria-label="Clear search" className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer rounded-full text-gray-700 dark:text-gray-200" onClick={() => setSearchValue('')} type="button">
                        <XIcon className="size-4" strokeWidth={2.5} />
                      </button>}
                  </div>
                  {!isResultsDismissed && searchQuery.length >= MIN_SEARCH_LENGTH && searchResults?.length ? <div className={cn('flex flex-col gap-2 rounded-xl border border-gray-150 bg-white p-3 shadow-card dark:border-gray-800 dark:bg-gray-900',
              // Floating dropdown on desktop; in the drawer it flows
              // inline so it can't get clipped by the drawer container.
              inDrawer ? 'mt-2' : 'absolute inset-x-0 top-full z-20 mt-2')}>
                      <ul aria-label="Channel search results" className="flex max-h-44 flex-col gap-1 overflow-y-auto">
                        {searchResults.map(result => <li key={result.snippet?.channelId}>
                            <button className="flex w-full cursor-pointer items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-50 focus-visible:bg-gray-50 dark:focus-visible:bg-gray-800 dark:hover:bg-gray-800" onClick={() => handleSelectChannel(result.snippet)} type="button">
                              <Image alt={`Channel thumbnail for ${result.snippet?.title}`} className="size-10 shrink-0 rounded-full bg-gray-200 object-cover dark:bg-gray-700" height={40} onError={e => {
                        e.currentTarget.src = PLACEHOLDER_IMG_URL;
                      }} src={result.snippet?.thumbnails?.default?.url || PLACEHOLDER_IMG_URL} width={40} />
                              <span className="truncate text-base text-gray-950 dark:text-white">
                                {result.snippet?.title}
                              </span>
                            </button>
                          </li>)}
                      </ul>
                      <div className="border-gray-150 border-t dark:border-gray-800" />
                      <div className="rounded-lg border border-pink-100 bg-pink-50 px-3.5 py-2.5 dark:border-pink-900 dark:bg-pink-950">
                        <p className="text-gray-950 text-sm dark:text-white">
                          Can't find your channel?{' '}
                          <button className="inline-flex cursor-pointer items-center gap-1 font-semibold text-pink-600 underline dark:text-white" onClick={() => handleTabChange('channel-id')} type="button">
                            Use your channel ID instead
                            <ArrowRight className="size-4" />
                          </button>
                        </p>
                      </div>
                    </div> : null}
                </div>}
              {safelistCta(selectedChannel)}
            </motion.div>
          </TabsContent>

          <TabsContent className="mt-5 data-[state=inactive]:hidden" value="channel-id" data-sentry-element="TabsContent" data-sentry-source-file="SafelistChannelPromptContent.tsx">
            <motion.div animate={{
            opacity: 1
          }} className="flex flex-col gap-2" initial={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} data-sentry-element="motion.div" data-sentry-source-file="SafelistChannelPromptContent.tsx">
              <p className="font-semibold text-gray-950 text-lg dark:text-white">
                Channel ID
              </p>
              {channelFromId ? <ChannelBox channel={channelFromId} onChange={() => setChannelIdValue('')} /> : <div className="relative">
                  <Input autoComplete="off" className={cn(!!channelIdValue && 'pr-10')} maxLength={CHANNEL_ID_LENGTH} name="youtube_channel_id" onChange={event => setChannelIdValue(event.target.value)} placeholder="Your channel ID" type="text" value={channelIdValue} />
                  {!!channelIdValue && <button aria-label="Clear channel ID" className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer rounded-full text-gray-700 dark:text-gray-200" onClick={() => setChannelIdValue('')} type="button">
                      <XIcon className="size-4" strokeWidth={2.5} />
                    </button>}
                </div>}
              <div className="flex items-center gap-2 rounded-lg border border-pink-100 bg-pink-50 px-3.5 py-2.5 dark:border-pink-900 dark:bg-pink-950">
                <Info className="size-5 shrink-0 text-gray-950 dark:text-white" data-sentry-element="Info" data-sentry-source-file="SafelistChannelPromptContent.tsx" />
                <p className="text-gray-950 text-sm dark:text-white">
                  Not sure where to find it?{' '}
                  <a className="font-semibold underline" href={CHANNEL_ID_HELP_URL} rel="noreferrer" target="_blank">
                    Learn how
                  </a>
                </p>
              </div>
              {safelistCta(channelFromId)}
            </motion.div>
          </TabsContent>
        </Tabs>

        {!!error && <p className="text-red-600 text-sm dark:text-red-400">{error}</p>}

        {!hasSelectedChannel && <button className={cn('mx-auto cursor-pointer rounded-lg px-4 pt-2 text-gray-950 underline dark:text-white', inDrawer ? 'text-sm' : 'text-base')} onClick={handleOptOut} type="button">
            Don't show this message again
          </button>}
      </div>
    </motion.div>;
};